import { BlockComponentFactory } from '../BlockFactory'
import Radio, { RadioChangeEvent } from 'antd/lib/radio'
import { RadioBlockProps } from './block-props.types'
import { LabelBlock } from './LabelBlock'
import { LABEL_FONT_STYLE } from '../../constants'
import { useMemo } from 'react'
import { useBlockListeners } from '../BlockHooks'
import { useBlockEngine } from '../BlockEngineContext'
import { toCSSSize } from './block-utils'

const OPTION_LABEL_FONT_STYLE: React.CSSProperties = {
    ...LABEL_FONT_STYLE,
    fontSize: '12px',
}

export const RadioBlockComponent = BlockComponentFactory.createBlockComponent<RadioBlockProps>(
    props => {
        const {
            label,
            infoText,
            required,
            width,
            options = [],
            direction = 'vertical',
            visibility = 'visible',
            disabled = false,
            style,
            value,
        } = props

        const isEditable = useBlockEngine().blockEngine.getEditableSignal().value

        const [onChangeListener] = useBlockListeners<(value: string) => void>('onChange')

        const containerStyle = useMemo<React.CSSProperties>(
            () => ({
                ...style,
                display: visibility === 'hidden' ? 'none' : 'flex',
                flexDirection: 'column',
                gap: '8px',
                width: toCSSSize(width) ?? 'auto',
            }),
            [style, visibility, width]
        )

        const groupStyle = useMemo<React.CSSProperties>(
            () => ({
                display: 'flex',
                flexDirection: direction === 'horizontal' ? 'row' : 'column',
                gap: direction === 'horizontal' ? '16px' : '8px',
                flexWrap: 'wrap',
            }),
            [direction]
        )

        const normalizedValue = useMemo<string | undefined>(() => {
            if (typeof value === 'string' && value.trim() !== '') {
                return value
            }
            return undefined
        }, [value])

        const defaultCheckedValue = useMemo<string | undefined>(
            () => options.find(option => option.checked)?.value,
            [options]
        )

        const evaluatedValue = normalizedValue ?? defaultCheckedValue

        const radioOptions = useMemo(
            () =>
                options.map(option => ({
                    label: option.value,
                    value: option.value,
                    style: OPTION_LABEL_FONT_STYLE,
                })),
            [options]
        )

        const handleChange = (e: RadioChangeEvent) => {
            onChangeListener?.value?.(e.target.value)
        }

        return (
            <div style={containerStyle}>
                <LabelBlock
                    label={label}
                    required={required}
                    infoText={infoText}
                    labelStyle={LABEL_FONT_STYLE}
                />

                <Radio.Group
                    options={radioOptions}
                    disabled={!isEditable || disabled}
                    style={groupStyle}
                    value={evaluatedValue}
                    onChange={handleChange}
                />
            </div>
        )
    }
)
